import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Button, Space, Tag, Input, message, Alert, Spin, Row, Col,
    Divider, Tooltip, Popconfirm, Empty
} from 'antd';
import {
    ReloadOutlined, SaveOutlined, PlusOutlined, CodeOutlined,
    AppstoreAddOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';

const { TextArea } = Input;
const { Search } = Input;

/**
 * PHP 配置管理:整份 php.ini 编辑 + 扩展安装/启用/禁用
 * 所有操作走当前容器活动 PHP 版本(切版本后需刷新)。
 */
const PhpConfigManager = ({ containerId }) => {
    const { t } = useTranslation();
    const [iniContent, setIniContent] = useState('');
    const [iniVersion, setIniVersion] = useState('');
    const [iniLoading, setIniLoading] = useState(false);
    const [iniSaving, setIniSaving] = useState(false);
    const [iniDirty, setIniDirty] = useState(false);

    const [extensions, setExtensions] = useState([]);
    const [extLoading, setExtLoading] = useState(false);
    const [installing, setInstalling] = useState(false);
    const [newExt, setNewExt] = useState('');

    // ---- php.ini ----
    const loadIni = useCallback(async () => {
        setIniLoading(true);
        const res = await hostService.getPhpIniRaw(containerId);
        setIniLoading(false);
        if (res.success) {
            setIniContent(res.data?.content || '');
            setIniVersion(res.data?.version || '');
            setIniDirty(false);
        } else {
            message.error(res.error || t('Failed to read php.ini'));
        }
    }, [containerId]);

    const saveIni = async () => {
        setIniSaving(true);
        const res = await hostService.setPhpIniRaw(containerId, iniContent);
        setIniSaving(false);
        if (res.success) {
            message.success(t('php.ini saved, PHP-FPM restarted'));
            setIniDirty(false);
        } else {
            message.error(res.error || t('Failed to save'));
        }
    };

    // ---- 扩展 ----
    const loadExtensions = useCallback(async () => {
        setExtLoading(true);
        const res = await hostService.getPhpExtensions(containerId);
        setExtLoading(false);
        if (res.success) {
            setExtensions(Array.isArray(res.data) ? res.data : []);
        } else {
            message.error(res.error || t('Failed to load the extension list'));
        }
    }, [containerId]);

    const installExt = async () => {
        const ext = (newExt || '').trim().toLowerCase();
        if (!ext) { message.warning(t('Please enter a file extension')); return; }
        if (!/^[a-z0-9_-]{1,40}$/.test(ext)) { message.error(t('Invalid extension name (only lowercase letters, numbers, _, and - allowed)')); return; }
        setInstalling(true);
        const res = await hostService.installPhpExtension(containerId, ext);
        setInstalling(false);
        if (res.success) {
            message.success(t('Extension {{ext}} installed', { ext }));
            setNewExt('');
            loadExtensions();
        } else {
            message.error(res.error || t('Installation failed'));
        }
    };

    const toggleExt = async (ext, enable) => {
        const res = await hostService.togglePhpExtension(containerId, ext, enable);
        if (res.success) {
            message.success(enable ? t('{{ext}} enabled', { ext }) : t('{{ext}} disabled', { ext }));
            loadExtensions();
        } else {
            message.error(res.error || t('Operation failed'));
        }
    };

    useEffect(() => {
        loadIni();
        loadExtensions();
    }, [loadIni, loadExtensions]);

    return (
        <div>
            <Alert
                message={t('Settings apply to the container\'s currently active PHP version')}
                description={t('After switching PHP versions, click Refresh to reload the corresponding php.ini and extensions. PHP-FPM will automatically reload gracefully after you save changes.')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            {/* PHP 扩展管理 */}
            <Card
                title={<><AppstoreAddOutlined /> {t('PHP extensions')} {iniVersion && <Tag color="blue">PHP {iniVersion.replace(/^(\d)(\d)$/, '$1.$2')}</Tag>}</>}
                extra={<Button icon={<ReloadOutlined />} size="small" onClick={loadExtensions} loading={extLoading}>{t('Refresh')}</Button>}
                style={{ marginBottom: 16 }}
            >
                <Space.Compact style={{ width: '100%', maxWidth: 420, marginBottom: 16 }}>
                    <Input
                        placeholder={t('Extension name, e.g. redis / imap / soap / gmp')}
                        value={newExt}
                        onChange={(e) => setNewExt(e.target.value)}
                        onPressEnter={installExt}
                        allowClear
                    />
                    <Button type="primary" icon={<PlusOutlined />} onClick={installExt} loading={installing}>
                        {t('Install')}
                    </Button>
                </Space.Compact>

                <Spin spinning={extLoading}>
                    {extensions.length === 0 ? (
                        <Empty description={t('No extension information yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <Space size={[8, 8]} wrap>
                            {extensions.map((ext) => (
                                <Popconfirm
                                    key={ext}
                                    title={t('Disable extension {{ext}}?', { ext })}
                                    description={t('Disabling core extensions may cause PHP errors. Please proceed carefully.')}
                                    okText={t('Disable')}
                                    cancelText={t('Cancel')}
                                    onConfirm={() => toggleExt(ext, false)}
                                >
                                    <Tag color="green" style={{ cursor: 'pointer', margin: 0 }}>
                                        {ext}
                                    </Tag>
                                </Popconfirm>
                            ))}
                        </Space>
                    )}
                </Spin>
                <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                    {t('Loaded extensions appear in green. Click to disable. Installed extensions come from official Alpine php{{iniVersion}}-* packages.', { iniVersion: iniVersion || 'XX' })}
                </div>
            </Card>

            {/* php.ini 编辑器 */}
            <Card
                title={<><CodeOutlined /> php.ini {iniDirty && <Tag color="orange">{t('Unsaved')}</Tag>}</>}
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} size="small" onClick={loadIni} loading={iniLoading}>
                            {t('Reload')}
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} size="small" onClick={saveIni}
                            loading={iniSaving} disabled={!iniDirty}>
                            {t('Save')}
                        </Button>
                    </Space>
                }
            >
                <Spin spinning={iniLoading}>
                    <TextArea
                        value={iniContent}
                        onChange={(e) => { setIniContent(e.target.value); setIniDirty(true); }}
                        rows={22}
                        spellCheck={false}
                        style={{ fontFamily: 'Menlo, Consolas, monospace', fontSize: 12, whiteSpace: 'pre' }}
                    />
                </Spin>
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                    {t('Edit the full php.ini for the current PHP version. It is automatically backed up as php.ini.bak inside the container before saving.')}
                </div>
            </Card>
        </div>
    );
};

export default PhpConfigManager;
